import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { Button } from "common/buttons";
import { Box } from "common/components/Box";
import { useConfirmDialog } from "common/components/confirm-dialog/use-confirm-dialog";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { NormalTabbedLayout } from "common/components/layouts/NormalTabbedLayout";
import { LinkButton } from "common/components/links/LinkButton";
import { Section } from "common/components/Section";
import { useMyStudyProgrammes } from "common/context/ExtendedStudyProgrammesContext";
import { urls } from "common/urls";
import { SelectExamSyllabi } from "domain/exam-syllabus/SelectExamSyllabi";
import { ConsentForm } from "domain/profile/ConsentForm";
import { SelectStudyProgrammes } from "domain/study-programme/SelectStudyProgrammes";
import React from "react";

const REQUEST_PERSONAL_INFO = gql`
  mutation RequestPersonalInfo {
    requestPersonalInfo {
      user {
        id
      }
    }
  }
`;

const REQUEST_ACCOUNT_DELETE = gql`
  mutation DeleteAccount {
    requestDeleteUserAccount {
      user {
        id
      }
    }
  }
`;

export const ProfilePage: React.FC = () => {
  const { state: myStudyProgrammes } = useMyStudyProgrammes();
  const { ConfirmBox, closeConfirmation, showConfirm } = useConfirmDialog();

  const [
    requestPersonalInfo,
    { error: requestPersonalInfoError, loading: requestPersonalInfoIsLoading },
  ] = useMutation(REQUEST_PERSONAL_INFO);

  const [
    requestAccountDelete,
    {
      error: requestAccountDeleteError,
      loading: requestAccountDeleteIsLoading,
    },
  ] = useMutation(REQUEST_ACCOUNT_DELETE);

  async function sendPersonalInfo() {
    await requestPersonalInfo();
    showConfirm({
      text: "Vi vil behandle forespørselen din, og sende deg din persondata så raskt som mulig.",
      okButtonText: "ok",
      okButtonAction: closeConfirmation,
    });
  }

  async function deleteAccount() {
    await requestAccountDelete();
    showConfirm({
      text: "Vi vil behandle forespørselen din, og vil slette din konto så raskt som mulig.",
      okButtonText: "ok",
      okButtonAction: closeConfirmation,
    });
  }

  return (
    <NormalTabbedLayout
      title="Profil"
      navigationTabs={[
        {
          title: "Mitt studie",
          path: urls.profileStudies,
          content: (
            <Box>
              <Section title="Mitt studie" withMargin={false}>
                <SelectStudyProgrammes />
                <SelectExamSyllabi
                  studyProgrammes={myStudyProgrammes.studyProgrammes ?? []}
                />
              </Section>
            </Box>
          ),
        },
        {
          title: "Personvern",
          path: urls.profilePrivacy,
          content: (
            <>
              <ConsentForm />
              <Section title="Persondata">
                <Button
                  kind="secondary"
                  onClick={() => {
                    showConfirm({
                      text:
                        "Ved å klikke på 'Send' vil du motta all personlig informasjon vi har om deg på" +
                        " e-posten knyttet til denne kontoen. Dette er en manuell prosess, og vi ber deg om" +
                        " å ikke misbruke denne funksjonaliteten.",
                      okButtonText: "Send",
                      okButtonAction: sendPersonalInfo,
                      cancelButtonText: "Avbryt",
                    });
                  }}
                >
                  Hent persondata
                </Button>
                <Button
                  kind="secondary"
                  onClick={() => {
                    showConfirm({
                      text:
                        "Ved å klikke på 'Slett' vil vi slette deg og din brukerinformasjon fra vårt system. " +
                        "Dette er permanent, og vi ber deg om at du tenker nøye gjennom dette valget.",
                      okButtonText: "Slett",
                      okButtonAction: deleteAccount,
                      cancelButtonText: "Avbryt",
                    });
                  }}
                >
                  Slett meg
                </Button>
                {ConfirmBox}
                <MutationLoadingOrError
                  loading={requestAccountDeleteIsLoading}
                  error={requestAccountDeleteError}
                />
                <MutationLoadingOrError
                  loading={requestPersonalInfoIsLoading}
                  error={requestPersonalInfoError}
                />
              </Section>
              <div style={{ marginTop: "2rem" }}>
                <LinkButton to={urls.termsOfUse}>
                  Les brukervilkår her
                </LinkButton>
              </div>
            </>
          ),
        },
      ]}
    />
  );
};
